import { useMutation } from '@apollo/client';
import { useState, useEffect, useCallback } from 'react';
import { initNethone, loadNethone } from 'nethone';
import CookiesManager from 'ggApp/utils/CookiesManager';

import { PAYMENT_SENSITIVE_FIELDS } from 'ggApp/modules/payment/constants';
import { SECRET_FIELDS } from 'ggApp/modules/auth/v3/constants/fieldIds';

import CreateCheckoutAttemptMutation from 'ggApp/modules/checkout/queries/CreateCheckoutAttemptMutation.graphql';

interface NethoneProps {
    orderNumber: string;
    userId: string | undefined;
}

export const useNethone = ({
    orderNumber,
    userId,
}: NethoneProps): {
    checkoutAttemptReference: string | null;
    isNethoneCrashed: boolean;
} => {
    const [isNethoneLoaded, setIsNethoneLoaded] = useState(false);
    const [isNethoneInitialized, setIsNethoneInitialized] = useState(false);
    const [checkoutAttemptReference, setCheckoutAttemptReference] = useState<string | null>(null);
    const [isNethoneCrashed, setNethoneCrashed] = useState<boolean>(false);

    const onGetCheckoutAttemptSuccess = useCallback(
        (data: { createCheckoutAttempt: { reference: string } }): void => {
            const { createCheckoutAttempt } = data || {};
            const { reference = null } = createCheckoutAttempt || {};
            if (!isNethoneInitialized && reference) {
                initNethone({
                    attemptReference: reference,
                    sensitiveFields: PAYMENT_SENSITIVE_FIELDS,
                    secretFields: SECRET_FIELDS,
                });
                setCheckoutAttemptReference(reference);
                setIsNethoneInitialized(true);
            }
        },
        [isNethoneInitialized],
    );

    const [getCheckoutAttempt] = useMutation(CreateCheckoutAttemptMutation, {
        onError: () => {
            setNethoneCrashed(true);
        },
        onCompleted: onGetCheckoutAttemptSuccess,
    });

    useEffect(() => {
        if (!isNethoneLoaded && orderNumber && userId) {
            const cookiesManager = new CookiesManager();
            const ggId = (cookiesManager as any).get('ggId');

            getCheckoutAttempt({
                variables: { orderNumber, sessionId: ggId },
            });

            loadNethone()
                .then(() => {
                    setIsNethoneLoaded(true);
                })
                .catch(() => {
                    setNethoneCrashed(true);
                });
        }
    }, [orderNumber, isNethoneLoaded, getCheckoutAttempt, userId]);

    return { checkoutAttemptReference, isNethoneCrashed };
};
